/**
 * Shared service-health derivation for header chrome, Overview, and System Health.
 * Down-count wins. Never report Healthy / 100% when any probed service is down.
 */

export type ServiceHealthStatus = 'Healthy' | 'Degraded' | 'Critical' | 'Unknown';
export type ServiceHealthVariant = 'healthy' | 'degraded' | 'down';

export type ServiceHealthCounts = {
  ok: number;
  degraded: number;
  down: number;
  total: number;
};

export type DerivedServiceHealth = ServiceHealthCounts & {
  status: ServiceHealthStatus;
  variant: ServiceHealthVariant;
  label: string;
};

export type ServiceHealthComponent = {
  status?: string | null;
  ok?: boolean;
  name?: string;
};

export type ServiceHealthPayload = {
  ok?: boolean;
  signal?: string | null;
  platform_build?: string | null;
  platform_stamp_status?: string | null;
  summary?: Partial<ServiceHealthCounts> | null;
  components?: Record<string, ServiceHealthComponent> | null;
  services?: Array<ServiceHealthComponent & { name?: string }> | null;
};

function normalizeComponentStatus(comp: ServiceHealthComponent | null | undefined): 'ok' | 'degraded' | 'down' {
  const status = String(comp?.status || '').toLowerCase();
  if (status === 'ok' || status === 'healthy') return 'ok';
  if (status === 'degraded') return 'degraded';
  if (status === 'down' || status === 'unhealthy' || status === 'critical' || status === 'error') return 'down';
  if (comp?.ok === true) return 'ok';
  if (comp?.ok === false) return 'down';
  return 'down';
}

export function recountServiceHealth(payload?: ServiceHealthPayload | null): ServiceHealthCounts | null {
  if (!payload) return null;

  const fromComponents = payload.components && Object.keys(payload.components).length > 0
    ? Object.values(payload.components)
    : null;
  const fromServices = !fromComponents && Array.isArray(payload.services) && payload.services.length > 0
    ? payload.services
    : null;
  const rows = fromComponents || fromServices;

  if (rows) {
    const counts: ServiceHealthCounts = { ok: 0, degraded: 0, down: 0, total: rows.length };
    for (const row of rows) {
      const status = normalizeComponentStatus(row);
      if (status === 'ok') counts.ok += 1;
      else if (status === 'degraded') counts.degraded += 1;
      else counts.down += 1;
    }
    return counts;
  }

  const summary = payload.summary;
  if (summary && (summary.total != null || summary.ok != null || summary.down != null || summary.degraded != null)) {
    const degraded = Number(summary.degraded || 0);
    const down = Number(summary.down || 0);
    const reportedOk = Number(summary.ok || 0);
    const total = Number(summary.total || 0) || reportedOk + degraded + down;
    // Down-count wins if summary.ok was padded to total (the header 27/27 lie).
    const ok = (down > 0 || degraded > 0) && reportedOk >= total
      ? Math.max(0, total - down - degraded)
      : reportedOk;
    return { ok, degraded, down, total };
  }

  return null;
}

export function deriveServiceHealth(payload?: ServiceHealthPayload | null): DerivedServiceHealth {
  const counted = recountServiceHealth(payload);

  if (counted && counted.total > 0) {
    // Down-count is authoritative even if summary.ok was padded to total.
    const notHealthy = counted.down > 0 || counted.degraded > 0 || counted.ok < counted.total;
    const status: ServiceHealthStatus = !notHealthy
      ? 'Healthy'
      : counted.ok > 0
        ? 'Degraded'
        : 'Critical';
    const variant: ServiceHealthVariant = status === 'Healthy'
      ? 'healthy'
      : status === 'Degraded'
        ? 'degraded'
        : 'down';
    return {
      ...counted,
      status,
      variant,
      label: `${status} (${counted.ok}/${counted.total})`,
    };
  }

  const signal = String(payload?.signal || '').toLowerCase();
  if (signal === 'ok' || (payload?.ok === true && !payload?.summary && !payload?.components)) {
    return {
      ok: 0, degraded: 0, down: 0, total: 0,
      status: 'Healthy',
      variant: 'healthy',
      label: 'Healthy',
    };
  }
  if (signal === 'degraded') {
    return {
      ok: 0, degraded: 0, down: 0, total: 0,
      status: 'Degraded',
      variant: 'degraded',
      label: 'Degraded',
    };
  }
  if (signal === 'down' || payload?.ok === false) {
    return {
      ok: 0, degraded: 0, down: 0, total: 0,
      status: 'Critical',
      variant: 'down',
      label: 'Critical',
    };
  }

  return {
    ok: 0, degraded: 0, down: 0, total: 0,
    status: 'Unknown',
    variant: 'degraded',
    label: 'Unknown',
  };
}

export function serviceHealthScore(health: DerivedServiceHealth, mcpScore = 100): number {
  if (health.total <= 0) {
    return health.status === 'Healthy' ? mcpScore : Math.min(mcpScore, health.status === 'Unknown' ? mcpScore : 0);
  }
  const serviceScore = Math.round((100 * health.ok) / health.total);
  return Math.min(mcpScore, serviceScore);
}

export function presentServingTag(value?: string | null): string | null {
  if (value == null) return null;
  const tag = String(value).trim();
  if (!tag || tag === 'unknown' || tag === 'null' || tag === 'undefined') return null;
  return tag;
}
